import React, { useState, useEffect, useCallback } from 'react';
import { Star, X, Smile, Meh, Frown, MessageSquare, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';

// Google Review URL for Pont10
const GOOGLE_REVIEW_URL = 'https://g.page/r/Cagg-G83XN6cEBM/review';

// Default texts (editable from admin)
const DEFAULT_TEXTS = {
  es: {
    bannerTitle: '¿Te está gustando la experiencia?',
    bannerButton: 'Dejar reseña en Google',
    thankYouTitle: 'Gracias por elegir Pont10',
    thankYouSubtitle: 'Si te ha gustado la experiencia, tu reseña nos ayuda muchísimo.',
    thankYouButton: 'Dejar reseña en Google',
    ratingQuestion: '¿Cómo ha sido tu experiencia?',
    ratingExcellent: 'Excelente',
    ratingGood: 'Buena',
    ratingImprove: 'Podemos mejorar',
    feedbackTitle: 'Cuéntanos qué podemos mejorar',
    feedbackPlaceholder: 'Tu opinión nos ayuda a mejorar...',
    feedbackSubmit: 'Enviar feedback',
    feedbackThanks: '¡Gracias por tu feedback!',
    later: 'Quizás más tarde',
    close: 'Cerrar'
  },
  en: {
    bannerTitle: 'Are you enjoying the experience?',
    bannerButton: 'Leave a Google review',
    thankYouTitle: 'Thank you for choosing Pont10',
    thankYouSubtitle: 'If you enjoyed the experience, your review helps us a lot.',
    thankYouButton: 'Leave a Google review',
    ratingQuestion: 'How was your experience?',
    ratingExcellent: 'Excellent',
    ratingGood: 'Good',
    ratingImprove: 'Could be better',
    feedbackTitle: 'Tell us how we can improve',
    feedbackPlaceholder: 'Your feedback helps us improve...',
    feedbackSubmit: 'Send feedback',
    feedbackThanks: 'Thank you for your feedback!',
    later: 'Maybe later',
    close: 'Close'
  },
  cat: {
    bannerTitle: 'T\'està agradant l\'experiència?',
    bannerButton: 'Deixar ressenya a Google',
    thankYouTitle: 'Gràcies per triar Pont10',
    thankYouSubtitle: 'Si t\'ha agradat l\'experiència, la teva ressenya ens ajuda moltíssim.',
    thankYouButton: 'Deixar ressenya a Google',
    ratingQuestion: 'Com ha estat la teva experiència?',
    ratingExcellent: 'Excel·lent',
    ratingGood: 'Bona',
    ratingImprove: 'Podem millorar',
    feedbackTitle: 'Explica\'ns què podem millorar',
    feedbackPlaceholder: 'La teva opinió ens ajuda a millorar...',
    feedbackSubmit: 'Enviar feedback',
    feedbackThanks: 'Gràcies pel teu feedback!',
    later: 'Potser més tard',
    close: 'Tancar'
  }
};

// Storage keys
const REVIEW_DISMISSED_KEY = 'pont10_review_dismissed';
const REVIEW_COMPLETED_KEY = 'pont10_review_completed';
const FEEDBACK_SENT_KEY = 'pont10_feedback_sent';

// Check if review prompt should be shown
const shouldShowReviewPrompt = () => {
  const dismissed = sessionStorage.getItem(REVIEW_DISMISSED_KEY);
  const completed = localStorage.getItem(REVIEW_COMPLETED_KEY);
  return !dismissed && !completed;
};

// Mark as dismissed for this session
const dismissReview = () => {
  sessionStorage.setItem(REVIEW_DISMISSED_KEY, 'true');
};

// Mark as completed (don't show again)
const completeReview = () => {
  localStorage.setItem(REVIEW_COMPLETED_KEY, 'true');
};

// =============================================
// REVIEW BANNER (Subtle floating banner)
// =============================================
export const ReviewBanner = ({ onClose, customTexts = {} }) => {
  const { language } = useLanguage();
  const texts = { ...DEFAULT_TEXTS[language] || DEFAULT_TEXTS.es, ...customTexts };
  const [showRating, setShowRating] = useState(false);

  const handleReviewClick = () => {
    setShowRating(true);
  };

  const handleRating = (rating) => {
    if (rating === 'improve') {
      // Show internal feedback form
      onClose('feedback');
    } else {
      // Redirect to Google Reviews
      completeReview();
      window.open(GOOGLE_REVIEW_URL, '_blank');
      onClose('completed');
    }
  };

  const handleDismiss = () => {
    dismissReview();
    onClose('dismissed');
  };

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 animate-slide-up"
      data-testid="review-banner"
    >
      <div 
        className="rounded-2xl p-4 shadow-xl border"
        style={{ 
          backgroundColor: 'var(--sand-50)',
          borderColor: 'rgba(196, 149, 106, 0.3)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
        }}
      >
        {!showRating ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#B78C3C20' }}
                >
                  <Star size={20} style={{ color: '#B78C3C' }} fill="#B78C3C" />
                </div>
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--stone-800)' }}>
                    {texts.bannerTitle}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-1 rounded-full hover:bg-stone-100"
              >
                <X size={16} className="text-stone-400" />
              </button>
            </div>
            <button
              onClick={handleReviewClick}
              className="w-full mt-3 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ backgroundColor: '#B78C3C', color: 'white' }}
              data-testid="review-banner-button"
            >
              <Star size={16} fill="white" />
              {texts.bannerButton}
            </button>
            <button
              onClick={handleDismiss}
              className="w-full mt-2 py-1.5 text-xs text-stone-400 hover:text-stone-600"
            >
              {texts.later}
            </button>
          </>
        ) : (
          <RatingSelector texts={texts} onSelect={handleRating} onClose={handleDismiss} />
        )}
      </div>
    </div>
  );
};

// =============================================
// RATING SELECTOR (Pre-filter)
// =============================================
const RatingSelector = ({ texts, onSelect, onClose }) => {
  return (
    <div className="text-center">
      <p className="font-medium mb-4" style={{ color: 'var(--stone-800)' }}>
        {texts.ratingQuestion}
      </p>
      <div className="flex justify-center gap-4 mb-3">
        <button
          onClick={() => onSelect('excellent')}
          className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
          data-testid="rating-excellent"
        >
          <span className="text-3xl">😀</span>
          <span className="text-xs" style={{ color: 'var(--gold)' }}>{texts.ratingExcellent}</span>
        </button>
        <button
          onClick={() => onSelect('good')}
          className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
          data-testid="rating-good"
        >
          <span className="text-3xl">🙂</span>
          <span className="text-xs" style={{ color: '#B78C3C' }}>{texts.ratingGood}</span>
        </button>
        <button
          onClick={() => onSelect('improve')}
          className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
          data-testid="rating-improve"
        >
          <span className="text-3xl">😐</span>
          <span className="text-xs" style={{ color: 'var(--stone-500)' }}>{texts.ratingImprove}</span>
        </button>
      </div>
      <button
        onClick={onClose}
        className="text-xs text-stone-400 hover:text-stone-600"
      >
        {texts.close}
      </button>
    </div>
  );
};

// =============================================
// THANK YOU MODAL (After "Show to Waiter")
// =============================================
export const ThankYouReviewModal = ({ isOpen, onClose, customTexts = {} }) => {
  const { language } = useLanguage();
  const texts = { ...DEFAULT_TEXTS[language] || DEFAULT_TEXTS.es, ...customTexts };
  const [step, setStep] = useState('rating'); // 'rating', 'feedback', 'thanks'
  const [feedback, setFeedback] = useState('');

  if (!isOpen) return null;

  const handleRating = (rating) => {
    if (rating === 'improve') {
      setStep('feedback');
    } else {
      completeReview();
      window.open(GOOGLE_REVIEW_URL, '_blank');
      onClose();
    }
  };

  const handleFeedbackSubmit = async () => {
    // TODO: Send feedback to backend
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback, language, timestamp: new Date().toISOString() })
      });
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
    localStorage.setItem(FEEDBACK_SENT_KEY, 'true');
    setStep('thanks');
    setTimeout(() => onClose(), 2000);
  };

  const handleDismiss = () => {
    dismissReview();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div 
        className="w-full max-w-sm rounded-3xl p-6 text-center relative"
        style={{ backgroundColor: 'var(--sand-50)' }}
        data-testid="thank-you-modal"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-stone-100"
        >
          <X size={20} className="text-stone-400" />
        </button>

        {step === 'rating' && (
          <>
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: '#B78C3C20' }}
            >
              <Star size={32} style={{ color: '#B78C3C' }} fill="#B78C3C" />
            </div>
            <h2 className="text-xl font-serif font-medium mb-2" style={{ color: 'var(--stone-800)' }}>
              {texts.thankYouTitle}
            </h2>
            <p className="text-sm mb-6" style={{ color: '#5C4A30' }}>
              {texts.thankYouSubtitle}
            </p>
            
            <p className="font-medium mb-4 text-sm" style={{ color: 'var(--stone-800)' }}>
              {texts.ratingQuestion}
            </p>
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={() => handleRating('excellent')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
              >
                <span className="text-4xl">😀</span>
                <span className="text-xs" style={{ color: 'var(--gold)' }}>{texts.ratingExcellent}</span>
              </button>
              <button
                onClick={() => handleRating('good')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
              >
                <span className="text-4xl">🙂</span>
                <span className="text-xs" style={{ color: '#B78C3C' }}>{texts.ratingGood}</span>
              </button>
              <button
                onClick={() => handleRating('improve')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-stone-100 transition-colors"
              >
                <span className="text-4xl">😐</span>
                <span className="text-xs" style={{ color: 'var(--stone-500)' }}>{texts.ratingImprove}</span>
              </button>
            </div>
            <button
              onClick={handleDismiss}
              className="text-xs text-stone-400 hover:text-stone-600"
            >
              {texts.later}
            </button>
          </>
        )}

        {step === 'feedback' && (
          <>
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: '#7A6B4E20' }}
            >
              <MessageSquare size={28} style={{ color: '#7A6B4E' }} />
            </div>
            <h2 className="text-lg font-serif font-medium mb-4" style={{ color: 'var(--stone-800)' }}>
              {texts.feedbackTitle}
            </h2>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={texts.feedbackPlaceholder}
              className="w-full p-3 rounded-xl border text-sm resize-none"
              style={{ 
                borderColor: 'rgba(45, 42, 38, 0.1)',
                backgroundColor: 'white',
                minHeight: '100px'
              }}
              data-testid="feedback-textarea"
            />
            <button
              onClick={handleFeedbackSubmit}
              disabled={!feedback.trim()}
              className="w-full mt-4 py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-50"
              style={{ backgroundColor: 'var(--stone-800)', color: 'white' }}
              data-testid="submit-feedback-btn"
            >
              {texts.feedbackSubmit}
            </button>
          </>
        )}

        {step === 'thanks' && (
          <>
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: '#B78C3C20' }}
            >
              <span className="text-3xl">💚</span>
            </div>
            <h2 className="text-lg font-serif font-medium" style={{ color: 'var(--stone-800)' }}>
              {texts.feedbackThanks}
            </h2>
          </>
        )}
      </div>
    </div>
  );
};

// =============================================
// REVIEW PROMPT MANAGER (Smart logic)
// =============================================
export const ReviewPromptManager = ({ enabled = true, customTexts = {} }) => {
  const { items, getItemCount } = useSelection();
  const [showBanner, setShowBanner] = useState(false);
  const [sessionStartTime] = useState(Date.now());
  const [productsOpened, setProductsOpened] = useState(0);

  // Track engagement metrics
  useEffect(() => {
    // Listen for product opens
    const handleProductOpen = () => {
      setProductsOpened(prev => prev + 1);
    };

    window.addEventListener('pont10_product_opened', handleProductOpen);
    return () => window.removeEventListener('pont10_product_opened', handleProductOpen);
  }, []);

  // Check if user is engaged enough to show review prompt
  const checkEngagement = useCallback(() => {
    if (!enabled || !shouldShowReviewPrompt()) return false;

    const itemCount = getItemCount();
    const sessionTime = (Date.now() - sessionStartTime) / 1000;

    // SMART CRITERIA (only show to engaged users):
    // - Added 2+ products
    // - OR session time > 90 seconds
    // - OR opened 4+ products
    const isEngaged = itemCount >= 2 || sessionTime > 90 || productsOpened >= 4;

    return isEngaged;
  }, [enabled, getItemCount, sessionStartTime, productsOpened]);

  // Check engagement periodically
  useEffect(() => {
    if (!enabled) return;

    const checkInterval = setInterval(() => {
      if (checkEngagement() && !showBanner) {
        setShowBanner(true);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkInterval);
  }, [enabled, checkEngagement, showBanner]);

  // Also check when items change
  useEffect(() => {
    if (checkEngagement() && !showBanner) {
      setShowBanner(true);
    }
  }, [items, checkEngagement, showBanner]);

  const handleBannerClose = (reason) => {
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return <ReviewBanner onClose={handleBannerClose} customTexts={customTexts} />;
};

// Export for use in other components
export { GOOGLE_REVIEW_URL, DEFAULT_TEXTS, shouldShowReviewPrompt, dismissReview, completeReview };
